import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import Input from '@lib/inputs/Input';
import RadioButtons from '@lib/inputs/RadioButtons';
import {
    GridStyledWrapper,
    StyledOtherPartyInputContainer,
    StyledPoliticalPartyRadioButtons,
} from '../voterRegistration.styled';
import useVoterRegistration from '../useVoterRegistration';
import { VoterRegistrationFormError } from '../VoterRegistrationFormError';
import { IVoterRegistrationFormValues } from '../voterRegistrationTypes';
import { PoliticalParty } from '../constants/voterRegistrationConstants';

function PoliticalPartySection() {
    const { VOTER_REGISTRATION_CHOICES } = useVoterRegistration();
    const intl = useIntl();

    const {
        watch,
        formState: { errors },
        setValue,
    } = useFormContext<IVoterRegistrationFormValues>();
    const politicalParty = watch('politicalParty');

    const handleFieldChange = (name: string, value: any) => {
        setValue(name as keyof IVoterRegistrationFormValues, value, {
            shouldValidate: true,
            shouldDirty: true,
        });
    };

    return (
        <div id="politicalParty-section">
            <FormattedMessage id="voterRegistration.form.politicalParty.header" tagName="h2" />
            <AlertMessage
                type={'info'}
                content={<FormattedMessage id="voterRegistration.form.politicalParty.alert" />}
                showIcon={true}
                icon={'info-circle'}
            />
            <fieldset className="gi-radio_buttons">
                <StyledPoliticalPartyRadioButtons>
                    <RadioButtons
                        legend={{
                            legendText: <FormattedMessage id="voterRegistration.form.politicalParty.select" />,
                        }}
                        label={{
                            id: 'politicalParty',
                            name: 'politicalParty',
                            showRequiredIcon: true,
                        }}
                        inputData={{
                            id: 'politicalParty',
                            isInputRequired: true,
                            options: VOTER_REGISTRATION_CHOICES.POLITICAL_PARTY,
                            type: 'radio_buttons',
                            value: politicalParty,
                        }}
                        inputActions={{
                            handleFieldChange: val => {
                                handleFieldChange('politicalParty', val);
                                handleFieldChange('otherPoliticalParty', '');
                            },
                        }}
                    />
                    <ConditionalRendering isTrue={politicalParty === PoliticalParty.OTHER}>
                        <StyledOtherPartyInputContainer>
                            <div>
                                <GridStyledWrapper>
                                    <div className="fullWidth">
                                        <Input
                                            label={{
                                                text: intl.formatMessage({
                                                    id: 'voterRegistration.form.politicalParty.otherParty.pleaseSpecify',
                                                }),
                                            }}
                                            inputData={{
                                                isInputRequired: true,
                                                id: 'otherPoliticalParty',
                                                name: 'otherPoliticalParty',
                                                value: watch('otherPoliticalParty'),
                                                type: INPUT_TYPES.TEXT_INPUT,
                                                helperClasses: ['w-100'],
                                            }}
                                            inputActions={{
                                                handleFieldChange: val => handleFieldChange('otherPoliticalParty', val),
                                            }}
                                        />
                                        <VoterRegistrationFormError
                                            name="otherPoliticalParty',"
                                            hasError={!!errors?.otherPoliticalParty}
                                            messageId={errors?.otherPoliticalParty?.message}
                                        />
                                    </div>
                                </GridStyledWrapper>
                            </div>
                        </StyledOtherPartyInputContainer>
                    </ConditionalRendering>
                </StyledPoliticalPartyRadioButtons>
                <VoterRegistrationFormError
                    name="politicalParty"
                    hasError={!!errors?.politicalParty?.message}
                    messageId={errors?.politicalParty?.message}
                />
            </fieldset>
        </div>
    );
}

export default PoliticalPartySection;
